/**
 * Tipo visual de un archivo adjunto: decide si se pinta como foto, video, audio o documento.
 *
 * Es UI sin dominio: el visor (`media-lightbox`) y las miniaturas (`evidence-tile`) lo usan
 * igual en tickets, cobranza o autorizaciones. Los tipos coinciden con los de
 * `tickets_adjuntos.tipo`, así que un `AdjuntoTipo` del portal de tickets encaja sin conversión.
 */
import type { TipoVisor } from "@/lib/tipoArchivoVisor";

export type MediaTipo = "foto" | "video" | "audio" | "documento";

const TIPOS: readonly MediaTipo[] = ["foto", "video", "audio", "documento"];

const EXT_FOTO = new Set(["jpg", "jpeg", "png", "gif", "webp", "heic", "heif", "bmp", "avif"]);
const EXT_VIDEO = new Set(["mp4", "mov", "webm", "m4v", "3gp", "avi", "mkv"]);
const EXT_AUDIO = new Set(["mp3", "m4a", "aac", "wav", "ogg", "oga", "opus", "weba"]);

/** Solo foto y video se ven en el visor; el audio se reproduce en la miniatura. */
export function esVisible(tipo: MediaTipo): boolean {
  return tipo === "foto" || tipo === "video";
}

function extension(texto?: string | null): string {
  if (!texto) return "";
  const limpio = texto.split(/[?#]/)[0];
  const punto = limpio.lastIndexOf(".");
  return punto >= 0 ? limpio.slice(punto + 1).toLowerCase() : "";
}

/**
 * Normaliza lo que trae la base (`tipo` libre, a veces un mime o nada) al tipo visual.
 *
 * Primero manda el `tipo` si ya es uno de los cuatro; luego el mime; luego la extensión del
 * nombre y, si no hay nombre, la de la URL. Lo que no se reconoce es documento: se abre aparte
 * en vez de intentar pintarlo como imagen rota.
 */
export function inferirTipoMedia(
  tipo?: string | null,
  nombre?: string | null,
  url?: string | null,
): MediaTipo {
  const t = (tipo ?? "").trim().toLowerCase();
  if ((TIPOS as readonly string[]).includes(t)) return t as MediaTipo;
  if (t.startsWith("image/") || t === "imagen" || t === "image") return "foto";
  if (t.startsWith("video/")) return "video";
  if (t.startsWith("audio/") || t === "voz") return "audio";

  const ext = extension(nombre) || extension(url);
  if (EXT_FOTO.has(ext)) return "foto";
  if (EXT_VIDEO.has(ext)) return "video";
  if (EXT_AUDIO.has(ext)) return "audio";
  return "documento";
}

/**
 * Qué hace una tecla dentro del visor (lightbox). Esc NO está aquí: la maneja el Dialog de Radix,
 * que solo cierra la capa de arriba (el visor) y no la hoja o el modal que lo abrió.
 */
export function accionTeclaVisor(key: string, index: number, total: number): number | null {
  if (total <= 1) return null;
  if (key === "ArrowRight") return (index + 1) % total;
  if (key === "ArrowLeft") return (index - 1 + total) % total;
  return null;
}

/**
 * Cómo se pinta un archivo dentro del visor, combinando el tipo que trae la fila con el tipo
 * real del archivo (`TipoVisor` de `lib/tipoArchivoVisor`, o `null` mientras se averigua).
 *
 * Un PDF puede llegar como "foto" (apps y bandejas que mandan todo adjunto como foto): va al
 * visor nativo del navegador, sin la barra − % + Ajustar, que es solo para imágenes.
 */
export type VistaVisor = "imagen" | "pdf" | "video" | "cargando";

export function vistaVisor(tipo: MediaTipo, tipoArchivo: TipoVisor | null): VistaVisor {
  if (tipo === "video") return "video";
  if (tipoArchivo === "pdf") return "pdf";
  if (tipoArchivo === "imagen") return "imagen";
  if (tipoArchivo === null) return "cargando";
  return tipo === "foto" ? "imagen" : "pdf";
}
